import { getAccessToken, signOut } from './okta';
import {
    SyncResponse,
    AutomationTask,
    OptionValueType,
    AutomationTaskEdit,
    TaskLog,
} from './tasks/types';
import { Webhook } from './webhooks/types';

interface Route {
    action: string;
    controller: string;
}

interface ExceptionJSON {
    details: string;
    endpoint: string;
    headers: Record<string, string[]>;
    path: string;
    routeValues: Route;
}

interface ErrorJSON {
    detail: string;
    exception: ExceptionJSON;
    status: number;
    title: string;
    traceId: string;
    type: string;
}

export class AutomateError extends Error {
    json: ErrorJSON;
    constructor(json: ErrorJSON) {
        super('');
        this.json = json;
    }
}

export const fetchAndParse = async <T>(
    url: string,
    method: 'get' | 'put' | 'post' | 'delete',
    body?: unknown,
) => {
    const response = await send(url, method, body);
    const parsedResponse = await response.json();

    if (response.status >= 400) {
        throw new AutomateError(parsedResponse);
    }

    return parsedResponse as T;
};

export const fetchAndParseAsText = async (
    url: string,
    method: 'get' | 'put' | 'post' | 'delete',
    body?: unknown,
) => {
    const response = await send(url, method, body);
    const responseText = await response.text();

    if (response.status >= 400) {
        throw new Error(responseText);
    }

    return responseText;
};

const fetchExpectingEmptyResponse = async (
    url: string,
    method: 'get' | 'put' | 'post' | 'delete',
    body?: unknown,
) => {
    const response = await send(url, method, body);

    if (!response.ok) {
        throw new AutomateError(await response.json());
    }

    return;
};

const send = async (url: string, method: 'get' | 'put' | 'post' | 'delete', body?: unknown) => {
    const accessToken = await getAccessToken();

    const response = await fetch(`${window.origin}${url}`, {
        method,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken?.accessToken}`,
        },
        body: body ? JSON.stringify(body) : undefined,
    });

    if (response.status === 401) {
        signOut();
    }

    return response;
};

export const sync = async (
    tasks: AutomationTaskEdit[],
    hash: string | null,
    profileImageDataBase64: string | null,
    includeProfileData: boolean,
): Promise<SyncResponse> =>
    fetchAndParse(`/api/sync`, 'put', { tasks, hash, profileImageDataBase64, includeProfileData });

export const test = async (
    task: AutomationTaskEdit,
    outputs: Record<string, string>,
): Promise<void> => {
    await fetchAndParseAsText(`/api/task/test`, 'post', { task, outputs });
    return;
};

export const askBoomiAI = async (prompt: string) =>
    fetchAndParse<AutomationTask>('/api/ai', 'post', prompt);

export const deleteConnectorToken = async (connectorId: string) =>
    fetchExpectingEmptyResponse(`/api/connector/${connectorId}/token`, 'delete', null);

export const redirectToOAuthUrl = async (
    connectorId: string,
    scope: string,
    options?: Record<string, OptionValueType>,
) => {
    const href = await fetchAndParseAsText(
        `/connector/${connectorId}/oauth/authenticateoptions?scope=${scope}`,
        'post',
        options ? JSON.stringify(options) : null,
    );
    window.location.href = href;
};

export const submitBasicAuth = async (
    connectorId: string | undefined,
    options?: { options: Record<string, OptionValueType> },
): Promise<void> =>
    fetchExpectingEmptyResponse(`/connector/${connectorId}/basicauth/callback`, 'put', options);

export const submitApiKey = async (
    connectorId: string | undefined,
    options?: { options: Record<string, OptionValueType> },
): Promise<void> =>
    fetchExpectingEmptyResponse(`/connector/${connectorId}/apikey/callback`, 'put', options);

export const submitCustomConnectorKeys = async (
    connectorId: string | undefined,
    options?: { options: Record<string, OptionValueType> },
): Promise<void> =>
    fetchExpectingEmptyResponse(`/connector/${connectorId}/custom/callback`, 'put', options);

export const markNotificationsAsRead = async (notifications: Array<string>) =>
    fetchExpectingEmptyResponse(`/api/event/notifications`, 'put', notifications);

export const deleteReadNotifications = async () =>
    fetchExpectingEmptyResponse(`/api/event/notifications`, 'delete', null);

export const getWebhooks = async (): Promise<Record<string, Webhook[] | null>> =>
    await fetchAndParse(`/api/webhook`, 'get', null);

export const deleteWebhook = async (connectorId: string, id: string) =>
    await fetchAndParse(`/api/webhook/${connectorId}/${id}`, 'delete', null);

export const getTaskLogs = async (taskId: string): Promise<TaskLog[]> =>
    await fetchAndParse(`/api/tasklogs/${taskId}`, 'get', null);
